import React, {useCallback, useEffect, useMemo, useState} from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import {DOCUMENT_DIMENSIONS_SELECT_MENU_Z_INDEX} from "../constants";

const NORMALIZED_FIELDS = [
    "volume",
    "gross_weight",
    "net_weight",
    "qty_per_packaging",
    "length",
    "width",
    "height",
    "radius",
];

const normalizeMetricValue = (value) => {
    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
        return 0;
    }
    return Number(numeric.toFixed(6));
};

const buildMetricsSignature = (metrics) => (
    NORMALIZED_FIELDS
        .map((field) => normalizeMetricValue(metrics?.[field]))
        .join("|")
);

const formatDimensionLabel = (dimension) => {
    if (!dimension) {
        return "";
    }
    if (dimension.reference) {
        return dimension.reference;
    }
    const parts = [
        dimension.length ? `${normalizeMetricValue(dimension.length)}L` : null,
        dimension.width ? `${normalizeMetricValue(dimension.width)}W` : null,
        dimension.height ? `${normalizeMetricValue(dimension.height)}H` : null,
        dimension.qty_per_packaging ? `${normalizeMetricValue(dimension.qty_per_packaging)}Q` : null,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(" · ") : "";
};

const findMatchingOption = (options, metrics) => {
    if (!Array.isArray(options) || options.length === 0) {
        return null;
    }
    const exactMatch = options.find((option) => {
        const data = option?.data ?? {};
        return NORMALIZED_FIELDS.every((field) => (
            normalizeMetricValue(data?.[field]) === normalizeMetricValue(metrics?.[field])
        ));
    }) ?? null;

    if (exactMatch) {
        return exactMatch;
    }
    return options.find((option) => option?.data?.default_dimension) ?? null;
};

const ReadOnlyDimensionsCell = ({dimensions, metrics, selectLabel}) => {
    const options = Array.isArray(dimensions)
        ? dimensions.map((dimension) => ({
            id: dimension.id,
            value: dimension.id,
            label: formatDimensionLabel(dimension),
            data: dimension,
        }))
        : [];

    if (options.length === 0) {
        return null;
    }

    const match = findMatchingOption(options, metrics);
    const resolvedLabel = match?.label
        ?? formatDimensionLabel(match?.data)
        ?? formatDimensionLabel(metrics)
        ?? selectLabel
        ?? "-";

    return (
        <span className="block truncate text-sm text-gray-900">
            {resolvedLabel}
        </span>
    );
};

const EditableDimensionsCell = ({dimensions, metrics, onSelect, selectLabel}) => {
    const options = useMemo(() => (
        Array.isArray(dimensions)
            ? dimensions.map((dimension) => ({
                id: dimension.id,
                value: dimension.id,
                label: formatDimensionLabel(dimension),
                data: dimension,
            }))
            : []
    ), [dimensions]);

    const metricsSignature = useMemo(() => buildMetricsSignature(metrics ?? {}), [metrics]);
    const [value, setValue] = useState(() => findMatchingOption(options, metrics));

    useEffect(() => {
        if (options.length === 0) {
            setValue(null);
            return;
        }
        const match = findMatchingOption(options, metrics);
        setValue((previous) => {
            if (match) {
                return previous?.id === match.id ? previous : match;
            }
            return previous ? null : previous;
        });
    }, [metrics, metricsSignature, options]);

    const handleChange = useCallback((nextValue) => {
        if (!nextValue || nextValue?.id === value?.id) {
            return;
        }
        setValue(nextValue);
        if (typeof onSelect === "function") {
            onSelect(nextValue);
        }
    }, [onSelect, value]);

    const selectStyles = useMemo(() => ({
        control: (base) => ({
            ...base,
            backgroundColor: "transparent",
            border: "none",
            boxShadow: "none",
            minHeight: "auto",
            cursor: "pointer",
        }),
        valueContainer: (base) => ({
            ...base,
            padding: 0,
        }),
        singleValue: (base) => ({
            ...base,
            color: "#111827",
            fontSize: "0.875rem",
            lineHeight: "1.25rem",
        }),
        placeholder: (base) => ({
            ...base,
            color: "#9CA3AF",
            fontSize: "0.875rem",
        }),
        dropdownIndicator: (base) => ({
            ...base,
            padding: 0,
            color: "#9CA3AF",
        }),
        indicatorSeparator: () => ({display: "none"}),
        menu: (base) => ({
            ...base,
            zIndex: DOCUMENT_DIMENSIONS_SELECT_MENU_Z_INDEX,
        }),
        menuList: (base) => ({
            ...base,
            padding: 0,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused ? "#EEF2FF" : "transparent",
            color: "#111827",
            fontSize: "0.875rem",
        }),
        input: (base) => ({
            ...base,
            margin: 0,
            padding: 0,
        }),
        clearIndicator: (base) => ({
            ...base,
            padding: 0,
        }),
        container: (base) => ({
            ...base,
            width: "100%",
        }),
    }), []);

    const menuPortalTarget = useMemo(() => (
        typeof window !== "undefined" ? window.document.body : undefined
    ), []);

    if (!Array.isArray(options) || options.length === 0) {
        return null;
    }

    return (
        <Select
            classNamePrefix="document-body-dimensions"
            value={value}
            onChange={handleChange}
            options={options}
            isClearable={false}
            isSearchable={false}
            styles={selectStyles}
            placeholder={selectLabel}
            menuPortalTarget={menuPortalTarget}
        />
    );
};

const DocumentBodyDimensionsCell = (props) => (
    props.isEditable
        ? <EditableDimensionsCell {...props} />
        : <ReadOnlyDimensionsCell {...props} />
);

DocumentBodyDimensionsCell.propTypes = {
    dimensions: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
        reference: PropTypes.string,
        volume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        gross_weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        net_weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        qty_per_packaging: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        length: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })),
    metrics: PropTypes.shape({
        volume: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        gross_weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        net_weight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        qty_per_packaging: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        length: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        radius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    }),
    onSelect: PropTypes.func.isRequired,
    selectLabel: PropTypes.string.isRequired,
    isEditable: PropTypes.bool,
};

DocumentBodyDimensionsCell.defaultProps = {
    dimensions: [],
    metrics: null,
    isEditable: true,
};

export default DocumentBodyDimensionsCell;
